/** Blocs annuaires dynamiques : hébergements, tourisme, tarifs, partenaires, espaces, événements, chevaux. */
import { websiteUrl, instagramUrl } from "../../lib/externalLinks";
import React, { Suspense, lazy } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { buildEngineUrl, trackAmenitizClick, DEFAULT_BOOKING_URL } from "../AmenitizAvailability";

const TourismExplorer = lazy(() => import("../cms/TourismExplorer"));
const ActivityCard = lazy(() => import("../cms/TourismExplorer").then((m) => ({ default: m.ActivityCard })));

// Grille dynamique d'hébergements — Phase 2 filtres UX
export function AccommodationsGridBlock({ data }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [liveAvail, setLiveAvail] = React.useState({ rooms: {}, booking_url: null });
  React.useEffect(() => {
    api.get("/public/amenitiz/availability-summary")
      .then((r) => { if (r.data?.status === "ok") setLiveAvail(r.data); })
      .catch(() => {});
  }, []);
  const [filters, setFilters] = React.useState({
    capacity_min: "", kitchen: false, pmr: false, pets: false, event: false,
    group: false, ac: false,
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    const params = {};
    if (data.parent_slug) params.parent_slug = data.parent_slug;
    if (data.kind) params.kind = data.kind;
    if (filters.capacity_min) params.capacity_min = filters.capacity_min;
    if (filters.kitchen) params.kitchen = true;
    if (filters.pmr) params.pmr = true;
    if (filters.pets) params.pets = true;
    if (filters.event) params.event = true;
    if (filters.group) params.group = true;
    if (filters.ac) params.ac = true;
    try {
      const { data: r } = await api.get("/public/accommodation-units", { params });
      setItems(r.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [filters, data.parent_slug, data.kind]);
  React.useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const eur = (c) => c == null ? "" : `${(c / 100).toLocaleString("fr-FR")} €`;

  return (
    <section className="py-16" data-testid="accommodations-grid">
      {data.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or text-center mb-2">{data.eyebrow}</div>}
      {data.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine text-center">{data.title}</h2>}
      {data.subtitle && <p className="text-center text-ardoise/70 mt-2 max-w-2xl mx-auto">{data.subtitle}</p>}
      <div className="mt-8 flex flex-wrap gap-2 items-center justify-center bg-sable/30 p-4 border border-pierre">
        <label className="flex items-center gap-2 text-xs">
          <span className="text-ardoise/70">Personnes :</span>
          <select value={filters.capacity_min}
                  onChange={(e) => setF("capacity_min", e.target.value)}
                  data-testid="filter-capacity"
                  className="px-2 py-1 border border-pierre bg-white text-sm">
            <option value="">Tous</option>
            <option value="2">2+</option>
            <option value="4">4+</option>
            <option value="6">6+</option>
            <option value="10">10+</option>
          </select>
        </label>
        {[
          ["kitchen", "Cuisine"], ["ac", "Climatisation"],
          ["pmr", "PMR"], ["pets", "Animaux"],
          ["group", "Groupe"], ["event", "Événement"],
        ].map(([k, lab]) => (
          <label key={k} className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-ardoise/70 px-3 py-1 border border-pierre bg-white cursor-pointer hover:border-or">
            <input type="checkbox" checked={filters[k]} onChange={(e) => setF(k, e.target.checked)}
                   data-testid={`filter-${k}`} />
            {lab}
          </label>
        ))}
        <button onClick={() => setFilters({ capacity_min: "", kitchen: false, pmr: false, pets: false, event: false, group: false, ac: false })}
                className="text-[10px] uppercase tracking-widest text-or/80 hover:text-domaine px-3">
          Réinitialiser
        </button>
      </div>
      {loading ? (
        <div className="text-center py-16 text-ardoise/50">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-ardoise/60 italic" data-testid="no-results">
          Aucun hébergement ne correspond à vos critères. Réessayez avec moins de filtres ou <a href="/nous-contacter" className="text-or underline">contactez-nous</a>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {items.map((a) => {
            const live = a.amenitiz_room_id ? liveAvail.rooms[a.amenitiz_room_id] : null;
            return (
            <a key={a.id} href={a.page_path || "#"}
               data-testid={`accom-card-${a.slug}`}
               className="group border border-pierre bg-white flex flex-col hover:border-or transition-colors relative">
              {live && live.next7_available != null && (
                <span data-testid={`accom-avail-badge-${a.slug}`}
                      className={`absolute top-3 left-3 z-10 px-2 py-1 text-[10px] uppercase tracking-widest
                                  ${live.next7_available ? "bg-or text-domaine" : "bg-ardoise/80 text-lin"}`}>
                  {live.next7_available ? "Disponible" : "Complet 7 j"}
                </span>
              )}
              {a.cover_image_url && (
                <img src={a.cover_image_url} alt={a.cover_image_alt || a.name} loading="lazy"
                     className="w-full aspect-[4/3] object-cover" />
              )}
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-domaine">{a.name}</h3>
                {a.short_description && (
                  <p className="text-sm text-ardoise/70 mt-2 line-clamp-3">{a.short_description}</p>
                )}
                <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ardoise/70">
                  {a.capacity && <li>👥 {a.capacity} pers.</li>}
                  {a.bedrooms && <li>🛏 {a.bedrooms} ch.</li>}
                  {a.has_kitchen && <li>🍳 Cuisine</li>}
                  {a.has_ac && <li>❄️ Clim.</li>}
                  {a.pets_allowed && <li>🐾 Animaux</li>}
                  {a.pmr_accessible && <li>♿ PMR</li>}
                </ul>
                <div className="flex-1" />
                <div className="mt-4 flex items-end justify-between">
                  {live?.min_price != null ? (
                    <div>
                      <span className="text-xs text-ardoise/60">à partir de</span>
                      <div className="text-lg font-serif text-domaine">
                        {live.min_price.toLocaleString("fr-FR")} €
                      </div>
                    </div>
                  ) : a.price_from_cents ? (
                    <div>
                      <span className="text-xs text-ardoise/60">à partir de</span>
                      <div className="text-lg font-serif text-domaine">{eur(a.price_from_cents)}</div>
                    </div>
                  ) : <span />}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`accom-book-btn-${a.slug}`}
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        if (a.amenitiz_room_id) {
                          window.location.href = `${a.page_path || "#"}#reserver`;
                        } else {
                          trackAmenitizClick("fallback_direct");
                          window.open(buildEngineUrl(a.booking_url || DEFAULT_BOOKING_URL, null, null, null), "_blank", "noopener");
                        }
                      }}
                      className="px-3 py-1.5 bg-domaine text-lin text-[10px] uppercase tracking-widest hover:bg-or hover:text-domaine transition-colors">
                      Réserver
                    </button>
                    <span className="text-[10px] uppercase tracking-widest text-or">Découvrir →</span>
                  </div>
                </div>
              </div>
            </a>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function AccommodationGridBlock({ limit }) {
  const [items, setItems] = React.useState([]);
  const { locale } = useI18n();
  React.useEffect(() => {
    api.get(`/public/accommodations?locale=${encodeURIComponent(locale)}`).then(r => setItems(r.data.slice(0, limit))).catch(() => {});
  }, [limit, locale]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="accommodation-grid">
      {items.map(a => (
        <a key={a.id} href={`/hebergements/${a.slug}`} className="group border border-pierre bg-lin hover:border-or transition-colors" data-testid={`accommodation-grid-card-${a.slug}`}>
          {a.cover_image_url && <img src={a.cover_image_url.startsWith("http") ? a.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${a.cover_image_url}`}
                                       alt={a.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />}
          <div className="p-4">
            <h3 className="font-serif text-xl text-domaine group-hover:text-or">{a.name}</h3>
            <p className="text-xs text-ardoise/60 mt-1">{a.capacity_max ? `Jusqu'à ${a.capacity_max} pers.` : ""}</p>
          </div>
        </a>
      ))}
    </div>
  );
}

export function AccommodationTreeBlock({ buildingSlug }) {
  const [tree, setTree] = React.useState([]);
  React.useEffect(() => {
    const params = buildingSlug ? { building_slug: buildingSlug } : {};
    api.get("/public/cms/accommodations/tree", { params }).then(r => setTree(r.data)).catch(() => {});
  }, [buildingSlug]);
  return (
    <div className="space-y-8" data-testid="accommodation-tree-block">
      {tree.map(b => (
        <div key={b.id} className="border border-pierre bg-white/50 p-6">
          <h3 className="text-2xl font-serif text-domaine">{b.name}</h3>
          {b.summary && <p className="text-sm text-ardoise mt-2">{b.summary}</p>}
          {b.children && b.children.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {b.children.map(c => (
                <a key={c.id} href={`/hebergements/${c.slug}`} className="border border-pierre bg-lin p-4 hover:border-or transition">
                  {c.cover_url && <img src={c.cover_url.startsWith("http") ? c.cover_url : `${process.env.REACT_APP_BACKEND_URL}${c.cover_url}`}
                                         alt={c.name} className="w-full aspect-[4/3] object-cover mb-3" loading="lazy" />}
                  <h4 className="font-serif text-domaine">{c.name}</h4>
                  <p className="text-xs text-ardoise/60 mt-1">
                    {c.capacity_max ? `${c.capacity_max} pers.` : ""}
                    {c.surface_sqm ? ` · ${c.surface_sqm} m²` : ""}
                  </p>
                  {c.base_price_cents && (
                    <p className="text-sm text-or mt-2">À partir de {(c.base_price_cents / 100).toFixed(0)} €</p>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function TourismGridBlock({ tag, limit }) {
  const [items, setItems] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    if (!tag) return;
    api.get("/public/tourism/activities", { params: { tag } }).then(r => {
      setItems(limit ? r.data.slice(0, limit) : r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [tag, limit]);
  if (!tag) return <Suspense fallback={null}><TourismExplorer /></Suspense>;
  if (!loaded || items.length === 0) return null;
  const sorted = [...items].sort((x, y) => (y.is_favorite - x.is_favorite) || (x.order_index - y.order_index));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tourism-grid-block">
      <Suspense fallback={null}>{sorted.map(a => <ActivityCard key={a.id} a={a} />)}</Suspense>
    </div>
  );
}

export function PricingGridBlock({ entity_type, entity_id, title }) {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    const params = {};
    if (entity_type) params.entity_type = entity_type;
    if (entity_id) params.entity_id = entity_id;
    api.get("/public/cms/pricing", { params }).then(r => setItems(r.data)).catch(() => {});
  }, [entity_type, entity_id]);
  if (items.length === 0) return null;
  const unitLabel = { nuit: "/ nuit", jour: "/ jour", personne: "/ personne", forfait: "", heure: "/ heure", semaine: "/ semaine", "week-end": "/ week-end" };
  return (
    <div className="border border-or/40 bg-sable/20 p-6" data-testid="pricing-grid-block">
      {title && <h3 className="text-2xl font-serif text-domaine mb-4 text-center">{title}</h3>}
      <div className="space-y-3">
        {items.map(p => (
          <div key={p.id} className="flex items-baseline justify-between border-b border-pierre/50 pb-2 last:border-0">
            <div>
              <p className="font-serif text-domaine">{p.name}</p>
              {p.notes && <p className="text-xs text-ardoise/60 italic">{p.notes}</p>}
            </div>
            <p className="text-lg font-medium text-or whitespace-nowrap">
              {p.amount_cents === 0 ? (
                <span>Sur devis</span>
              ) : (
                <>
                  {(p.amount_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: p.currency, maximumFractionDigits: 0 })}
                  <span className="text-xs text-ardoise/60 ml-1">{unitLabel[p.unit] || ""}</span>
                </>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Bloc "Annuaire Partenaires" ────────────────────────────────────────────
export function PartnersDirectoryBlock({ context }) {
  const [data, setData] = React.useState({ categories: [], total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [activeCat, setActiveCat] = React.useState("all");
  const { locale } = useI18n();

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/public/partners?context=${encodeURIComponent(context)}&locale=${encodeURIComponent(locale)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [context, locale]);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement des partenaires…</div>;
  if (data.total === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-pierre text-ardoise/60">
        Aucun partenaire à afficher pour le moment.
      </div>
    );
  }

  const visibleCategories = activeCat === "all"
    ? data.categories
    : data.categories.filter((c) => c.slug === activeCat);

  return (
    <div data-testid="partners-directory">
      {/* Filtre par catégorie */}
      {data.categories.length > 1 && (
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          <button
            onClick={() => setActiveCat("all")}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${
              activeCat === "all"
                ? "bg-domaine text-lin"
                : "border border-pierre text-ardoise hover:bg-domaine hover:text-lin"
            }`}
          >
            Tous ({data.total})
          </button>
          {data.categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActiveCat(c.slug)}
              className={`px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${
                activeCat === c.slug
                  ? "bg-domaine text-lin"
                  : "border border-pierre text-ardoise hover:bg-domaine hover:text-lin"
              }`}
            >
              {c.name} ({c.partners.length})
            </button>
          ))}
        </div>
      )}

      {/* Liste par catégorie */}
      <div className="space-y-10">
        {visibleCategories.map((cat) => (
          <div key={cat.slug}>
            <h3 className="text-xs uppercase tracking-[0.25em] text-domaine mb-4 pb-2 border-b border-pierre">
              {cat.name}
            </h3>
            <div className="flex flex-col divide-y divide-pierre/60">
              {cat.partners.map((p) => (
                <PartnerCard key={p.id} partner={p} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerCard({ partner }) {
  const p = partner;
  const { pathname } = useLocation();
  return (
    <div className="flex items-start gap-5 py-5" data-testid={`partner-card-${p.id}`}>
      <div className="w-24 h-24 flex-shrink-0 bg-white border border-pierre flex items-center justify-center overflow-hidden p-2">
        {p.logo_url ? (
          <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" loading="lazy" />
        ) : (
          <span className="text-[10px] text-ardoise/40 uppercase tracking-widest">Logo</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {p.is_featured && <span className="text-or text-sm">★</span>}
          <h4 className="font-serif text-lg text-ardoise">
            <Link to={`/partenaires/${p.slug}`} state={{ from: pathname }} className="hover:text-domaine transition-colors" data-testid={`partner-name-link-${p.id}`}>{p.name}</Link>
          </h4>
          {p.city && <span className="text-xs text-ardoise/60">· {p.city}</span>}
        </div>
        {p.short_description && (
          <p className="text-sm text-ardoise/80 leading-relaxed mb-2 max-w-2xl">{p.short_description}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ardoise/60">
          {p.price_hint && <span className="text-domaine">{p.price_hint}</span>}
          {p.instagram && (
            <a href={instagramUrl(p.instagram)} target="_blank" rel="noreferrer"
               className="hover:text-domaine">Instagram</a>
          )}
          {p.facebook && (
            <a href={p.facebook.startsWith("http") ? p.facebook : `https://facebook.com/${p.facebook.replace(/^@/, "")}`}
               target="_blank" rel="noreferrer"
               className="hover:text-domaine">Facebook</a>
          )}
          {p.email && <a href={`mailto:${p.email}`} className="hover:text-domaine">{p.email}</a>}
          {p.phone && <a href={`tel:${p.phone}`} className="hover:text-domaine">{p.phone}</a>}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col items-stretch gap-2">
        <Link
          to={`/partenaires/${p.slug}`}
          state={{ from: pathname }}
          className="px-4 py-2 text-center text-[11px] uppercase tracking-widest bg-domaine text-lin hover:bg-or hover:text-domaine transition-colors"
          data-testid={`partner-fiche-btn-${p.id}`}
        >
          Voir la fiche
        </Link>
        {p.website && (
          <a
            href={websiteUrl(p.website)}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 text-center text-[11px] uppercase tracking-widest border border-domaine text-domaine hover:bg-domaine hover:text-lin transition-colors"
            data-testid={`partner-visit-${p.id}`}
          >
            Voir le site
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Bloc "Événements / Stages" (dynamique) ────────────────────────────────
const EVENT_TYPE_LABELS = {
  equestre: "Stage équestre", course: "Course d'endurance", mariage: "Mariage",
  seminaire: "Séminaire", sejour: "Séjour", brunch: "Brunch", autre: "Événement",
};

function EventCard({ ev, past }) {
  const d = ev.starts_at ? new Date(ev.starts_at) : null;
  return (
    <a href={`/evenements/${ev.slug}`}
       className={`group block bg-lin border border-pierre overflow-hidden hover:shadow-lg transition-shadow ${past ? "opacity-80" : ""}`}
       data-testid={`event-card-${ev.slug}`}>
      <div className="aspect-[16/9] bg-sable/40 overflow-hidden relative">
        {ev.cover_image_url ? (
          <img src={ev.cover_image_url.startsWith("http") ? ev.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${ev.cover_image_url}`}
               alt={ev.title} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ardoise/30 text-xs uppercase tracking-widest">Photo</div>
        )}
        <span className="absolute top-3 left-3 px-3 py-1 text-[10px] uppercase tracking-widest bg-domaine/90 text-lin">
          {EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}
        </span>
        {past && (
          <span className="absolute top-3 right-3 px-3 py-1 text-[10px] uppercase tracking-widest bg-ardoise/80 text-lin">Archives</span>
        )}
      </div>
      <div className="p-5">
        {d && (
          <p className="text-[11px] uppercase tracking-widest text-or mb-2">
            {d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        )}
        <h3 className="font-serif text-xl text-domaine mb-2">{ev.title}</h3>
        {ev.summary && <p className="text-sm text-ardoise/80 leading-relaxed line-clamp-3">{ev.summary}</p>}
        <div className="flex items-center justify-between mt-3 text-xs text-ardoise/60">
          <span>{ev.location || ""}</span>
          {!ev.is_free && ev.price_cents ? (
            <span className="text-or">{(ev.price_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
          ) : ev.is_free ? <span className="text-domaine">Gratuit</span> : null}
        </div>
      </div>
    </a>
  );
}

export function EventsGridBlock({ d = {} }) {
  const [events, setEvents] = React.useState([]);
  const [archives, setArchives] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { locale } = useI18n();

  React.useEffect(() => {
    let cancelled = false;
    const params = { locale, limit: d.limit || 12, period: d.period || "upcoming" };
    if (d.event_type) params.event_type = d.event_type;
    const calls = [api.get("/public/events", { params })];
    if (d.show_archives && (d.period || "upcoming") === "upcoming") {
      calls.push(api.get("/public/events", { params: { ...params, period: "past" } }));
    }
    Promise.all(calls)
      .then(([up, past]) => {
        if (cancelled) return;
        setEvents(up.data || []);
        if (past) setArchives(past.data || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [d.event_type, d.period, d.limit, d.show_archives, locale]);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement…</div>;

  return (
    <div data-testid="events-grid">
      {events.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-pierre text-ardoise/60">
          {d.empty_text || "Aucun événement programmé pour le moment — contactez-nous pour connaître les prochaines dates."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((ev) => <EventCard key={ev.id} ev={ev} past={(d.period || "upcoming") === "past"} />)}
        </div>
      )}
      {archives.length > 0 && (
        <div className="mt-16">
          <p className="text-xs uppercase tracking-[0.25em] text-domaine mb-6 pb-2 border-b border-pierre">Éditions passées</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {archives.map((ev) => <EventCard key={ev.id} ev={ev} past />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bloc "Chevaux à Vendre" ───────────────────────────────────────────────
export function HorsesForSaleBlock() {
  const [horses, setHorses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    api.get("/public/horses")
      .then(({ data }) => {
        if (cancelled) return;
        setHorses((data || []).filter((h) =>
          h.is_public !== false &&
          (["for_sale", "reserved"].includes(h.sale_status) || h.is_for_sale)
        ));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement…</div>;

  return (
    <div data-testid="horses-for-sale">
      {horses.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-pierre text-ardoise/60">
          Aucun cheval à la vente actuellement.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {horses.map((h) => (
            <a
              key={h.id}
              href={`/haras/chevaux/${h.slug}`}
              className="group block bg-lin border border-pierre overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`horse-sale-${h.slug}`}
            >
              <div className="aspect-[4/3] bg-sable/40 overflow-hidden relative">
                {h.cover_image_url ? (
                  <img
                    src={h.cover_image_url.startsWith("http") ? h.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${h.cover_image_url}`}
                    alt={h.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ardoise/30 text-xs uppercase tracking-widest">Photo</div>
                )}
                {h.sale_status === "reserved" && (
                  <span className="absolute top-3 right-3 px-3 py-1 text-[10px] uppercase tracking-widest bg-or text-domaine">Réservé</span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif text-xl text-domaine">{h.name}</h3>
                  {h.sale_price_cents && (
                    <span className="text-sm text-or">
                      {(h.sale_price_cents / 100).toLocaleString("fr-FR", {
                        style: "currency", currency: "EUR", maximumFractionDigits: 0,
                      })}
                    </span>
                  )}
                </div>
                <p className="text-[11px] uppercase tracking-widest text-ardoise/60">
                  {[h.breed, h.discipline, h.height_cm ? `${h.height_cm} cm` : null].filter(Boolean).join(" · ")}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Carte Contact toujours affichée */}
      <div className="mt-12 border border-domaine bg-domaine/5 p-8 text-center">
        <h3 className="font-serif text-2xl text-domaine mb-3">Une question sur un cheval ?</h3>
        <p className="text-sm text-ardoise/80 max-w-xl mx-auto mb-5">
          Nos équipes se tiennent à votre disposition pour organiser une visite,
          répondre à vos questions et vous accompagner dans le choix du cheval qui vous correspond.
        </p>
        <a
          href="/nous-contacter"
          className="inline-block px-6 py-3 text-[11px] uppercase tracking-widest bg-domaine text-lin hover:opacity-90 transition-opacity"
          data-testid="horses-contact-cta"
        >
          Nous contacter
        </a>
      </div>
    </div>
  );
}

// ─── Grille de cartes réutilisable (3 colonnes, grosses photos) ─────────────
function EntityGrid({ items, basePath, testIdPrefix }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-pierre text-ardoise/60">
        Aucun contenu à afficher pour le moment.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item) => {
        const url = basePath ? `${basePath}/${item.slug || item.code}` : `#${item.slug || item.code}`;
        const cover = item.cover_image_url;
        const absCover = cover && cover.startsWith("http") ? cover : (cover ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : null);
        return (
          <a key={item.id} href={url}
             data-testid={`${testIdPrefix}-${item.slug || item.code}`}
             className="group block bg-lin border border-pierre overflow-hidden hover:shadow-lg transition-shadow">
            <div className="aspect-[4/3] bg-sable/40 overflow-hidden">
              {absCover ? (
                <img src={absCover} alt={item.name} loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-ardoise/30 text-xs uppercase tracking-widest">Photo</div>
              )}
            </div>
            <div className="p-5">
              <h3 className="font-serif text-xl text-domaine mb-1">{item.name}</h3>
              {(item.summary || item.description) && (
                <p className="text-sm text-ardoise/80 line-clamp-2">{item.summary || item.description}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-widest text-ardoise/60">
                {item.capacity_seated && <span>{item.capacity_seated} assis</span>}
                {item.capacity_standing && <span>{item.capacity_standing} debout</span>}
                {item.capacity_max && <span>{item.capacity_max} pers.</span>}
                {item.bedrooms && <span>{item.bedrooms} ch.</span>}
                {item.surface_sqm && <span>{item.surface_sqm} m²</span>}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ─── Bloc "Annuaire Espaces" ────────────────────────────────────────────────
export function VenuesDirectoryBlock({ context, basePath }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { pathname } = useLocation();
  const { locale } = useI18n();
  const derivedBase = basePath || pathname.replace(/\/$/, "");

  React.useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams();
    if (context) qs.set("context", context);
    qs.set("locale", locale);
    api.get(`/public/venues?${qs.toString()}`)
      .then(({ data }) => !cancelled && setItems(data || []))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [context, locale]);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement…</div>;
  return <EntityGrid items={items} basePath={derivedBase} testIdPrefix="venue" />;
}

// ─── Bloc "Annuaire Hébergements" ───────────────────────────────────────────
export function AccommodationsDirectoryBlock({ context, basePath }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { pathname } = useLocation();
  const { locale } = useI18n();
  const derivedBase = basePath || pathname.replace(/\/$/, "");

  React.useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams();
    if (context) qs.set("context", context);
    qs.set("locale", locale);
    api.get(`/public/accommodations-list?${qs.toString()}`)
      .then(({ data }) => !cancelled && setItems(data || []))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [context, locale]);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement…</div>;
  return <EntityGrid items={items} basePath={derivedBase} testIdPrefix="acc" />;
}
